import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import {
  getMobileUser,
  getMobileUsers,
  deleteMobileUser,
  getUsers,
  getClientsBySearch,
} from "../../actions/auth";
import { Button, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { getUserFromLocalStorage } from "utilities/user";
import AppSelect from "../../components/ui/AppSelect";
import CopyValue from "../../components/ui/CopyValue";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// ── Helpers ────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  (name || "").trim()
    ? name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()
    : "?";

// A phone OTP sign-in creates the account with a number and no name; the apps
// ask for one afterwards, skippably. Until the customer gives it, the row says
// so rather than showing a dash that reads like a broken record.
const NO_NAME_YET = "No name yet";

// What the row reads as contact. A customer always has at least one of these
// (phone sign-ups have no email, email sign-ups have no phone), so the cell
// shows whichever exists and the search covers both.
const contactLines = (u) => {
  const phone = (u?.phoneNumber || "").trim();
  const email = (u?.email || "").trim();
  return { phone, email };
};

const AVATAR_COLORS = ["#117a44", "#0d6efd", "#c8880a", "#6f42c1", "#0dcaf0", "#dc3545", "#20c997"];
const getAvatarColor = (name = "") =>
  (name || "").trim() ? AVATAR_COLORS[name.trim().charCodeAt(0) % AVATAR_COLORS.length] : "#b7c4d4";

// ── Platform badge ─────────────────────────────────────────────────────────
const PLATFORM_CFG = {
  android: { bg: "#e8f5e9", color: "#2e7d32", icon: "fa-android",  label: "Android" },
  ios:     { bg: "#e3f2fd", color: "#1565c0", icon: "fa-apple",    label: "iOS"     },
  web:     { bg: "#fff3e0", color: "#bf360c", icon: "fa-globe",    label: "Web"     },
};

const PlatformBadge = ({ platform }) => {
  const cfg = PLATFORM_CFG[(platform || "").toLowerCase()];
  if (!cfg) return <span style={{ color: "#bbb", fontSize: 12 }}>—</span>;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: cfg.bg, color: cfg.color,
      padding: "3px 10px", borderRadius: 20,
      fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.03em",
    }}>
      <i className={`fab ${cfg.icon}`} style={{ fontSize: 10 }} />
      {cfg.label}
    </span>
  );
};

// ── Login-activity badge ───────────────────────────────────────────────────
/** True if the user has authenticated on web/app at least once (lastLogin or loginCount). */
const hasAppLoginActivity = (u) =>
  Boolean(u?.lastLogin) || Number(u?.loginCount) > 0;

const WalkInBadge = () => (
  <OverlayTrigger
    placement="top"
    overlay={
      <Tooltip>
        Created from an in-house / phone-in order without the customer signing in to the app.
      </Tooltip>
    }
  >
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginLeft: 6,
        background: "#fce4ec",
        color: "#ad1457",
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: "0.65rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        cursor: "help",
      }}
    >
      <i className="fas fa-store" style={{ fontSize: 9 }} />
      Walk-in
    </span>
  </OverlayTrigger>
);

const LoginBadge = ({ count }) => {
  if (count > 0) return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: "#e8f5e9", color: "#2e7d32",
      padding: "3px 10px", borderRadius: 20,
      fontSize: "0.72rem", fontWeight: 700,
    }}>
      <i className="fas fa-check-circle" style={{ fontSize: 9 }} />
      {count} login{count !== 1 ? "s" : ""}
    </span>
  );
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: "#f4f6f9", color: "#9aacbf",
      padding: "3px 10px", borderRadius: 20,
      fontSize: "0.72rem", fontWeight: 700,
    }}>
      <i className="fas fa-minus-circle" style={{ fontSize: 9 }} />
      Never
    </span>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const MobileUsers = () => {
  const { authorizedUsers, isLoading, userCreated } = useSelector((state) => state.auth);
  const user    = getUserFromLocalStorage();
  const userId  = user?.result?._id;
  const query   = useQuery();

  const dispatch = useDispatch();
  const history  = useHistory();

  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [searchClient, setSearchClient]         = useState("");
  const [filterPlatform, setFilterPlatform]     = useState("all");
  const [filterLoginStatus, setFilterLoginStatus] = useState("all");
  const [filterJoinPeriod, setFilterJoinPeriod] = useState("all");
  const [sortField, setSortField]               = useState("createdAt");
  const [sortDir, setSortDir]                   = useState("desc");
  const [showFilters, setShowFilters]           = useState(false);

  useEffect(() => {
    dispatch(getMobileUsers(user?.result._id, user?.result.user_role));
  }, [userCreated]);

  const handleDeleteClient = (clientId) => {
    dispatch(deleteMobileUser(clientId, user?.result?._id)).then(() => {
      dispatch(getUsers(user?.result?._id, user?.result?.user_role));
    });
    setShowDeleteModal(false);
  };

  const searchClients = () => {
    if (searchClient.trim()) {
      dispatch(getClientsBySearch({ searchClient, user_id: userId }));
      history.push(`/admin/mobileUsers?searchQuery=${searchClient || "none"}`);
    } else {
      history.push("/admin/mobileUsers");
    }
  };

  const handleKeyPress = (e) => { if (e.key === "Enter") searchClients(); };

  const handleInputClear = (e) => {
    if (e.target.value === "") {
      history.push("/admin/mobileUsers");
      dispatch(getUsers(userId, user?.result?.user_role));
      setSearchClient("");
    }
  };

  const resetFilters = () => {
    setFilterPlatform("all");
    setFilterLoginStatus("all");
    setFilterJoinPeriod("all");
    setSearchClient("");
    setSortField("createdAt");
    setSortDir("desc");
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  // ── Base list ──────────────────────────────────────────────────────────
  const allClients = useMemo(
    () => (authorizedUsers || [])
      .map((x) => ({ user: x.user || x }))
      .filter((x) => x.user.userType === "CLIENT"),
    [authorizedUsers]
  );

  // ── KPI counts ────────────────────────────────────────────────────────
  const androidCount = useMemo(() => allClients.filter(({ user: u }) => (u.lastLoginPlatform || "").toLowerCase() === "android").length, [allClients]);
  const iosCount     = useMemo(() => allClients.filter(({ user: u }) => (u.lastLoginPlatform || "").toLowerCase() === "ios").length, [allClients]);
  const activeCount  = useMemo(() => allClients.filter(({ user: u }) => hasAppLoginActivity(u)).length, [allClients]);

  // ── Filter + sort pipeline ────────────────────────────────────────────
  const filteredClients = useMemo(() => {
    const cutoff = filterJoinPeriod !== "all"
      ? moment().subtract(parseInt(filterJoinPeriod, 10), "days").toDate()
      : null;

    let result = allClients.filter(({ user: u }) => {
      if (searchClient.trim()) {
        const q = searchClient.toLowerCase().replace(/\s+/g, "");
        const hay = [u.name, u.email, u.phoneNumber, u.lastLoginPlatform].join(" ").toLowerCase().replace(/\s+/g, "");
        // Numbers are stored as +256..., staff type 077...: both spellings find the row.
        const asIntl = /^0\d+$/.test(q) ? `256${q.slice(1)}` : null;
        if (!hay.includes(q) && !(asIntl && hay.includes(asIntl))) return false;
      }
      if (filterPlatform === "unknown") { if (u.lastLoginPlatform) return false; }
      else if (filterPlatform !== "all") { if ((u.lastLoginPlatform || "").toLowerCase() !== filterPlatform) return false; }
      if (filterLoginStatus === "active" && !hasAppLoginActivity(u)) return false;
      if (filterLoginStatus === "never"  && hasAppLoginActivity(u)) return false;
      if (cutoff && new Date(u.createdAt) < cutoff) return false;
      return true;
    });

    result = [...result].sort((a, b) => {
      let valA = a.user[sortField] ?? "";
      let valB = b.user[sortField] ?? "";
      if (sortField === "createdAt" || sortField === "lastLogin") {
        valA = valA ? new Date(valA) : new Date(0);
        valB = valB ? new Date(valB) : new Date(0);
      } else if (sortField === "loginCount") {
        valA = Number(valA); valB = Number(valB);
      } else {
        valA = String(valA).toLowerCase(); valB = String(valB).toLowerCase();
      }
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [allClients, searchClient, filterPlatform, filterLoginStatus, filterJoinPeriod, sortField, sortDir]);

  const activeFiltersCount = [
    filterPlatform !== "all" ? filterPlatform : "",
    filterLoginStatus !== "all" ? filterLoginStatus : "",
    filterJoinPeriod !== "all" ? filterJoinPeriod : "",
  ].filter(Boolean).length;

  // ── Restricted view ────────────────────────────────────────────────────
  if (user?.result?.user_role === "CLIENT" || user?.result?.user_role === "CREATOR") {
    return (
      <div style={{ padding: 32, fontFamily: '"Segoe UI", system-ui, sans-serif' }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          background: "#fff3f4", border: "1.5px solid #f5c2c7",
          borderRadius: 10, padding: "16px 24px", color: "#c62828",
          fontSize: "0.95rem", fontWeight: 600,
        }}>
          <i className="fas fa-lock" style={{ fontSize: 20 }} />
          Restricted — Admins only
        </div>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────
  return (
    <>
      {isLoading && (
        <Spinner animation="grow" variant="success"
          style={{ position: "fixed", top: "50%", left: "50%", zIndex: 9999 }}
        />
      )}

      {/* Delete modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "2px solid #dc3545" }}>
          <Modal.Title>
            <i className="fas fa-trash" style={{ color: "#dc3545", marginRight: 8 }} />
            Confirm Client Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.9rem" }}>
          This action is <strong>irreversible</strong>. Are you sure you want to delete this client?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => handleDeleteClient(selectedClientId)}>Delete Client</Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .pbi-page { background: #f4f6f9; min-height: 100vh; padding: 24px 20px; font-family: "Segoe UI", system-ui, sans-serif; }

        /* KPI Cards — 4 cols desktop, 2 cols mobile */
        .pbi-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
        .pbi-kpi-card { background: #fff; border-radius: 10px; padding: 18px 20px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); border-top: 4px solid transparent; position: relative; overflow: hidden; }
        .pbi-kpi-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0; margin-bottom: 6px; }
        .pbi-kpi-value { font-size: 2rem; font-weight: 800; line-height: 1; margin-bottom: 4px; }
        .pbi-kpi-sub   { font-size: 0.75rem; color: #8a9ab0; }
        .pbi-kpi-icon  { position: absolute; right: 18px; top: 50%; transform: translateY(-50%); opacity: 0.1; }

        /* Panel */
        .pbi-panel { background: #fff; border-radius: 10px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); overflow: hidden; }
        .pbi-panel-header { background: linear-gradient(135deg, #1a2a3a 0%, #243447 100%); padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
        .pbi-panel-title { color: #fff; font-size: 1.05rem; font-weight: 700; margin: 0; display: flex; align-items: center; gap: 8px; }
        .pbi-panel-title i { color: #5bc5f5; }

        /* Filter bar */
        .pbi-filter-bar { padding: 12px 16px; background: #fafbfc; border-bottom: 1px solid #f0f2f5; }
        .pbi-filter-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .pbi-filter-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: #8a9ab0; white-space: nowrap; }
        .pbi-search-wrap { position: relative; flex: 1; min-width: 0; }
        .pbi-search-wrap .pbi-search-icon { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #9aacbf; font-size: 13px; pointer-events: none; }
        .pbi-search-wrap input { width: 100%; padding: 8px 34px 8px 35px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.83rem; outline: none; transition: border-color 0.2s; background: #fff; }
        .pbi-search-wrap input:focus { border-color: #5bc5f5; }
        .pbi-search-clear { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #9aacbf; cursor: pointer; padding: 2px; font-size: 12px; }
        .pbi-filter-select { padding: 6px 10px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.8rem; background: #fff; color: #2d3748; outline: none; transition: border-color 0.2s; cursor: pointer; height: 34px; }
        .pbi-filter-select:focus { border-color: #5bc5f5; }
        .pbi-filter-select.active { border-color: #5bc5f5; background: #f0f9ff; color: #1565c0; font-weight: 600; }
        .pbi-filter-toggle { height: 34px; padding: 0 14px; border: 1.5px solid #e4e8ef; border-radius: 8px; background: #fff; font-size: 0.8rem; font-weight: 600; color: #6b7c93; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: background 0.15s; white-space: nowrap; }
        .pbi-filter-toggle:hover, .pbi-filter-toggle.has-filters { background: #e8f4fd; border-color: #5bc5f5; color: #1565c0; }
        .pbi-filter-badge { background: #5bc5f5; color: #fff; width: 18px; height: 18px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.65rem; font-weight: 800; }
        .pbi-sort-btn { height: 34px; padding: 0 12px; border: 1.5px solid #e4e8ef; border-radius: 8px; background: #fff; color: #6b7c93; font-size: 0.8rem; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 5px; white-space: nowrap; }
        .pbi-sort-btn:hover, .pbi-sort-btn.active { background: #e8f4fd; border-color: #5bc5f5; color: #1565c0; }
        .pbi-filter-divider { width: 1px; height: 22px; background: #e4e8ef; margin: 0 2px; flex-shrink: 0; }
        .pbi-result-pill { display: flex; align-items: center; gap: 7px; background: #f0f2f5; color: #495057; padding: 6px 14px; border-radius: 20px; font-size: 0.78rem; font-weight: 600; border: 1px solid #e4e8ef; white-space: nowrap; }
        .pbi-result-pill .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
        .pbi-chip { display: inline-flex; align-items: center; gap: 5px; background: #e8f4fd; color: #1565c0; border: 1px solid #b3d9f7; padding: 3px 10px; border-radius: 20px; font-size: 0.74rem; font-weight: 600; }
        .pbi-chip-remove { background: none; border: none; cursor: pointer; padding: 0; color: #6aacdb; font-size: 11px; display: flex; align-items: center; margin-left: 2px; }
        .pbi-chip-remove:hover { color: #dc3545; }
        .pbi-clear-all { background: none; border: none; cursor: pointer; font-size: 0.74rem; font-weight: 600; color: #dc3545; padding: 3px 8px; border-radius: 6px; }
        .pbi-clear-all:hover { background: #f8d7da; }
        .pbi-chips-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; padding-top: 10px; }
        .pbi-expanded-filters { padding-top: 10px; border-top: 1px dashed #e4e8ef; margin-top: 8px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .pbi-filter-controls-row { display: contents; }

        /* Table — desktop */
        .pbi-table-scroll { overflow-x: auto; }
        table.pbi-table { width: 100%; border-collapse: collapse; }
        table.pbi-table thead tr { background: #f4f6f9; }
        table.pbi-table thead th { padding: 10px 14px; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0; border-bottom: 2px solid #e9ecef; white-space: nowrap; border-top: none; }
        table.pbi-table thead th:first-child { padding-left: 20px; }
        table.pbi-table thead th.sortable { cursor: pointer; user-select: none; }
        table.pbi-table thead th.sortable:hover { color: #5bc5f5; }
        table.pbi-table thead th.sort-active { color: #1565c0; }
        .sort-arrow { margin-left: 4px; font-size: 0.65rem; }
        table.pbi-table tbody tr { border-left: 3px solid transparent; transition: background 0.12s, border-color 0.12s; }
        table.pbi-table tbody tr:hover { background: #f7fbff; border-left-color: #5bc5f5; }
        table.pbi-table tbody td { padding: 11px 14px; font-size: 0.82rem; color: #2d3748; border-bottom: 1px solid #f0f2f5; vertical-align: middle; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px; }
        table.pbi-table tbody td:first-child { padding-left: 20px; }
        table.pbi-table tbody tr:last-child td { border-bottom: none; }

        /* Mobile card list — hidden on desktop */
        .pbi-card-list { display: none; padding: 12px; }
        .pbi-mobile-card {
          background: #fff; border-radius: 10px; padding: 14px 16px;
          border: 1px solid #e9ecef; border-left: 4px solid #5bc5f5;
          margin-bottom: 10px;
        }
        .pbi-mobile-card-top { display: flex; align-items: flex-start; gap: 12px; }
        .pbi-mobile-card-body { flex: 1; min-width: 0; }
        .pbi-mobile-card-name { font-weight: 700; font-size: 0.9rem; color: #1a2a3a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .pbi-mobile-card-email { font-size: 0.76rem; color: #6b7c93; margin: 2px 0 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .pbi-mobile-card-badges { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
        .pbi-mobile-card-meta { display: flex; align-items: center; justify-content: space-between; margin-top: 10px; padding-top: 8px; border-top: 1px solid #f0f2f5; }
        .pbi-mobile-card-date { font-size: 0.72rem; color: #9aacbf; }
        .pbi-mobile-card-actions { display: flex; gap: 6px; flex-shrink: 0; }

        /* Avatar */
        .pbi-avatar { width: 34px; height: 34px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; font-size: 0.72rem; font-weight: 800; color: #fff; flex-shrink: 0; margin-right: 9px; }
        .pbi-avatar-lg { width: 42px; height: 42px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; font-size: 0.85rem; font-weight: 800; color: #fff; flex-shrink: 0; }
        .pbi-name-cell { display: flex; align-items: center; }
        .pbi-name-text { font-weight: 600; color: #1a2a3a; }
        .pbi-name-sub  { font-size: 0.7rem; color: #9aacbf; }

        /* Action buttons */
        .pbi-actions { display: flex; gap: 5px; }
        .pbi-btn { width: 30px; height: 30px; border-radius: 7px; border: 1.5px solid; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; cursor: pointer; transition: background 0.15s, color 0.15s; background: transparent; }
        .pbi-btn-edit { border-color: #c8d6e5; color: #6b7c93; }
        .pbi-btn-edit:hover { background: #fff3cd; border-color: #ffc107; color: #856404; }
        .pbi-btn-del  { border-color: #f5c2c7; color: #dc3545; }
        .pbi-btn-del:hover  { background: #f8d7da; }

        /* Empty state */
        .pbi-empty { text-align: center; padding: 52px 20px; }
        .pbi-empty-icon { font-size: 36px; margin-bottom: 12px; opacity: 0.22; color: #8a9ab0; }
        .pbi-empty-msg { font-size: 0.88rem; color: #9aacbf; font-style: italic; }
        .pbi-empty-link { background: none; border: none; cursor: pointer; color: #0d6efd; font-size: 0.85rem; text-decoration: underline; margin-top: 6px; }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .pbi-page { padding: 16px 12px; }
          .pbi-kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 16px; }
          .pbi-kpi-card { padding: 14px 14px; }
          .pbi-kpi-value { font-size: 1.6rem; }
          .pbi-kpi-icon { display: none; }
          .pbi-panel-header { padding: 12px 14px; }
          .pbi-panel-title { font-size: 0.95rem; }
          .pbi-filter-bar { padding: 10px 12px; }
          .pbi-filter-divider { display: none; }
          .pbi-filter-label { display: none; }
          /* Search on its own full-width row, then controls row below */
          .pbi-filter-row { flex-direction: column; align-items: stretch; gap: 8px; }
          .pbi-search-wrap { flex: unset; width: 100%; }
          .pbi-filter-controls-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
          .pbi-filter-select { flex: 1; min-width: 0; }
          .pbi-result-pill-wrap { margin-left: auto; }
          /* Hide table, show card list */
          .pbi-table-scroll { display: none; }
          .pbi-card-list { display: block; }
        }

        @media (max-width: 480px) {
          .pbi-kpi-value { font-size: 1.4rem; }
          .pbi-kpi-label { font-size: 0.65rem; }
          .pbi-kpi-sub { display: none; }
        }
      `}</style>

      <div className="pbi-page">

        {/* ── KPI Cards ── */}
        <div className="pbi-kpi-grid">
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip>
                Total registered client accounts, including walk-in placeholder accounts created from in-house orders.
              </Tooltip>
            }
          >
            <div className="pbi-kpi-card" style={{ borderTopColor: "#0d6efd", cursor: "help" }}>
              <div className="pbi-kpi-icon"><i className="fas fa-mobile-alt" style={{ fontSize: 52, color: "#0d6efd" }} /></div>
              <div className="pbi-kpi-label">Total Clients</div>
              <div className="pbi-kpi-value" style={{ color: "#0d6efd" }}>{allClients.length}</div>
              <div className="pbi-kpi-sub">All registered users</div>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip>
                Clients with a recorded last sign-in or at least one counted login (web, email, Google, or phone OTP).
                Walk-in placeholder accounts created only from in-house orders do not count until they sign in.
              </Tooltip>
            }
          >
            <div className="pbi-kpi-card" style={{ borderTopColor: "#117a44", cursor: "help" }}>
              <div className="pbi-kpi-icon"><i className="fas fa-check-circle" style={{ fontSize: 52, color: "#117a44" }} /></div>
              <div className="pbi-kpi-label">Active Users</div>
              <div className="pbi-kpi-value" style={{ color: "#117a44" }}>{activeCount}</div>
              <div className="pbi-kpi-sub">Signed in on web or app at least once</div>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip>
                Clients whose last recorded login was on an Android device. Based on the <em>lastLoginPlatform</em> field captured at sign-in.
              </Tooltip>
            }
          >
            <div className="pbi-kpi-card" style={{ borderTopColor: "#2e7d32", cursor: "help" }}>
              <div className="pbi-kpi-icon"><i className="fab fa-android" style={{ fontSize: 52, color: "#2e7d32" }} /></div>
              <div className="pbi-kpi-label">Android</div>
              <div className="pbi-kpi-value" style={{ color: "#2e7d32" }}>{androidCount}</div>
              <div className="pbi-kpi-sub">Android platform users</div>
            </div>
          </OverlayTrigger>
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip>
                Clients whose last recorded login was on an iOS device (iPhone or iPad). Based on the <em>lastLoginPlatform</em> field captured at sign-in.
              </Tooltip>
            }
          >
            <div className="pbi-kpi-card" style={{ borderTopColor: "#1565c0", cursor: "help" }}>
              <div className="pbi-kpi-icon"><i className="fab fa-apple" style={{ fontSize: 52, color: "#1565c0" }} /></div>
              <div className="pbi-kpi-label">iOS</div>
              <div className="pbi-kpi-value" style={{ color: "#1565c0" }}>{iosCount}</div>
              <div className="pbi-kpi-sub">iOS platform users</div>
            </div>
          </OverlayTrigger>
        </div>

        {/* ── Main Panel ── */}
        <div className="pbi-panel">

          {/* Dark header */}
          <div className="pbi-panel-header">
            <div className="pbi-panel-title">
              <i className="fas fa-mobile-alt" />
              Mobile Clients Directory
            </div>
            <div className="pbi-result-pill" style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
              <span className="dot" style={{ background: "#5bc5f5" }} />
              {filteredClients.length} of {allClients.length} clients
            </div>
          </div>

          {/* Filter bar */}
          <div className="pbi-filter-bar">
            <div className="pbi-filter-row">
              {/* Search */}
              <div className="pbi-search-wrap">
                <i className="fas fa-search pbi-search-icon" />
                <input
                  type="text"
                  placeholder="Search by name, phone, email, platform"
                  value={searchClient}
                  onChange={(e) => { setSearchClient(e.target.value); handleInputClear(e); }}
                  onKeyDown={handleKeyPress}
                />
                {searchClient && (
                  <button className="pbi-search-clear" onClick={() => { setSearchClient(""); history.push("/admin/mobileUsers"); dispatch(getUsers(userId, user?.result?.user_role)); }}>
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>

              <div className="pbi-filter-controls-row">
                <div className="pbi-filter-divider" />

                {/* Filters toggle */}
                <button
                  className={`pbi-filter-toggle${activeFiltersCount > 0 ? " has-filters" : ""}`}
                  onClick={() => setShowFilters((v) => !v)}
                >
                  <i className="fas fa-sliders-h" />
                  Filters
                  {activeFiltersCount > 0 && <span className="pbi-filter-badge">{activeFiltersCount}</span>}
                  <i className={`fas fa-chevron-${showFilters ? "up" : "down"}`} style={{ fontSize: 10, marginLeft: 2 }} />
                </button>

                <div className="pbi-filter-divider" />

                {/* Sort */}
                <span className="pbi-filter-label">Sort</span>
                <AppSelect
                  className="pbi-filter-select"
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  style={{ minWidth: 120 }}
                >
                  <option value="createdAt">Date Joined</option>
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                  <option value="phoneNumber">Phone</option>
                  <option value="loginCount">Login Count</option>
                  <option value="lastLogin">Last Login</option>
                </AppSelect>
                <button
                  className={`pbi-sort-btn${sortDir === "desc" ? " active" : ""}`}
                  onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}
                  title="Toggle sort direction"
                >
                  <i className={`fas fa-sort-amount-${sortDir === "asc" ? "up" : "down"}`} />
                  {sortDir === "asc" ? "A → Z" : "Z → A"}
                </button>

                <div className="pbi-result-pill-wrap" style={{ marginLeft: "auto" }}>
                  <div className="pbi-result-pill">
                    <span className="dot" style={{ background: "#0d6efd" }} />
                    {filteredClients.length} client{filteredClients.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            </div>

            {/* Expanded filters */}
            {showFilters && (
              <div className="pbi-expanded-filters">
                <span className="pbi-filter-label">Platform</span>
                <AppSelect
                  className={`pbi-filter-select${filterPlatform !== "all" ? " active" : ""}`}
                  value={filterPlatform}
                  onChange={(e) => setFilterPlatform(e.target.value)}
                  style={{ minWidth: 150 }}
                >
                  <option value="all">All Platforms</option>
                  <option value="android">Android</option>
                  <option value="ios">iOS</option>
                  <option value="web">Web</option>
                  <option value="unknown">Unknown</option>
                </AppSelect>

                <div className="pbi-filter-divider" />

                <span className="pbi-filter-label">Login Status</span>
                <AppSelect
                  className={`pbi-filter-select${filterLoginStatus !== "all" ? " active" : ""}`}
                  value={filterLoginStatus}
                  onChange={(e) => setFilterLoginStatus(e.target.value)}
                  style={{ minWidth: 180 }}
                >
                  <option value="all">All Users</option>
                  <option value="active">Logged in at least once</option>
                  <option value="never">Never logged in</option>
                </AppSelect>

                <div className="pbi-filter-divider" />

                <span className="pbi-filter-label">Joined</span>
                <AppSelect
                  className={`pbi-filter-select${filterJoinPeriod !== "all" ? " active" : ""}`}
                  value={filterJoinPeriod}
                  onChange={(e) => setFilterJoinPeriod(e.target.value)}
                  style={{ minWidth: 150 }}
                >
                  <option value="all">All Time</option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 3 months</option>
                  <option value="365">Last year</option>
                </AppSelect>

                {activeFiltersCount > 0 && (
                  <button className="pbi-clear-all" onClick={resetFilters}>
                    <i className="fas fa-times-circle" style={{ marginRight: 4 }} />
                    Clear all
                  </button>
                )}
              </div>
            )}

            {/* Active filter chips */}
            {activeFiltersCount > 0 && (
              <div className="pbi-chips-row">
                <span className="pbi-filter-label">Active:</span>
                {filterPlatform !== "all" && (
                  <span className="pbi-chip">
                    <i className="fas fa-mobile-alt" style={{ fontSize: 10 }} />
                    {filterPlatform}
                    <button className="pbi-chip-remove" onClick={() => setFilterPlatform("all")}><i className="fas fa-times" /></button>
                  </span>
                )}
                {filterLoginStatus !== "all" && (
                  <span className="pbi-chip">
                    <i className="fas fa-sign-in-alt" style={{ fontSize: 10 }} />
                    {filterLoginStatus === "active" ? "Signed in (web/app)" : "No app sign-in"}
                    <button className="pbi-chip-remove" onClick={() => setFilterLoginStatus("all")}><i className="fas fa-times" /></button>
                  </span>
                )}
                {filterJoinPeriod !== "all" && (
                  <span className="pbi-chip">
                    <i className="fas fa-calendar" style={{ fontSize: 10 }} />
                    Last {filterJoinPeriod} days
                    <button className="pbi-chip-remove" onClick={() => setFilterJoinPeriod("all")}><i className="fas fa-times" /></button>
                  </span>
                )}
                <button className="pbi-clear-all" onClick={resetFilters}>
                  <i className="fas fa-times-circle" style={{ marginRight: 4 }} />
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* ── Desktop Table ── */}
          <div className="pbi-table-scroll">
            <table className="pbi-table">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>#</th>
                  {[
                    { label: "Name",       field: "name"       },
                    { label: "Contact",    field: "email"      },
                    { label: "Platform",   field: null         },
                    { label: "Last Login", field: "lastLogin"  },
                    { label: "Logins",     field: "loginCount" },
                    { label: "Created By", field: null         },
                    { label: "Joined",     field: "createdAt"  },
                    { label: "Actions",    field: null         },
                  ].map(({ label, field }) => (
                    <th
                      key={label}
                      className={`${field ? "sortable" : ""}${sortField === field ? " sort-active" : ""}`}
                      onClick={() => field && toggleSort(field)}
                    >
                      {label}
                      {field && (
                        <span className="sort-arrow">
                          {sortField === field ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="pbi-empty">
                        <div className="pbi-empty-icon"><i className="fas fa-mobile-alt" /></div>
                        <div className="pbi-empty-msg">No clients match the selected filters.</div>
                        <button className="pbi-empty-link" onClick={resetFilters}>Clear filters</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredClients.map(({ user: u }, index) => (
                    <tr key={u._id || index}>
                      <td style={{ color: "#9aacbf", fontWeight: 700, fontSize: "0.75rem" }}>{index + 1}</td>
                      <td>
                        <div className="pbi-name-cell">
                          <span className="pbi-avatar" style={{ background: getAvatarColor(u.name) }}>
                            {getInitials(u.name)}
                          </span>
                          <div>
                            <div className="pbi-name-text" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                              {u.name || <em style={{ color: "#8a9ab0", fontWeight: 500 }}>{NO_NAME_YET}</em>}
                              {u.isWalkIn ? <WalkInBadge /> : null}
                            </div>
                            <div className="pbi-name-sub">{u.isWalkIn ? "Walk-in / placeholder" : "Mobile Client"}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ color: "#4a5568" }}>
                        {(() => {
                          const { phone, email } = contactLines(u);
                          if (!phone && !email) return "—";
                          return (
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                              {phone && <CopyValue value={phone} mono />}
                              {email && <CopyValue value={email} max={32} mono={false} />}
                            </div>
                          );
                        })()}
                      </td>
                      <td><PlatformBadge platform={u.lastLoginPlatform} /></td>
                      <td style={{ color: "#6b7c93", fontSize: "0.8rem" }}>
                        {u.lastLogin ? moment(u.lastLogin).fromNow() : <em style={{ color: "#c8d6e5" }}>Never</em>}
                      </td>
                      <td><LoginBadge count={u.loginCount || 0} /></td>
                      <td style={{ fontSize: "0.78rem", color: "#8a9ab0" }}>
                        {u.createdBy ? (
                          <span title={`${u.createdBy.name || "Staff"} (${u.createdBy.email || ""})`}>
                            {u.createdBy.name || "—"}
                          </span>
                        ) : (
                          <em style={{ color: "#c8d6e5", fontSize: "0.75rem" }}>Self-registered</em>
                        )}
                      </td>
                      <td style={{ fontSize: "0.78rem", color: "#8a9ab0" }}>
                        <span title={moment(u.createdAt).format("DD MMM YYYY HH:mm")}>
                          {moment(u.createdAt).format("DD MMM YYYY")}
                        </span>
                        <div style={{ fontSize: "0.68rem", color: "#b0bec5" }}>{moment(u.createdAt).fromNow()}</div>
                      </td>
                      <td>
                        <div className="pbi-actions">
                          <OverlayTrigger placement="top" overlay={<Tooltip>Edit Client</Tooltip>}>
                            <button
                              className="pbi-btn pbi-btn-edit"
                              onClick={() => {
                                dispatch(getMobileUser(u._id, user?.result?._id, user?.result?.user_role));
                                history.push(`/admin/user-edit/${u._id}`);
                              }}
                            >
                              <i className="fas fa-edit" />
                            </button>
                          </OverlayTrigger>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Delete Client</Tooltip>}>
                            <button
                              className="pbi-btn pbi-btn-del"
                              onClick={() => { setSelectedClientId(u._id); setShowDeleteModal(true); }}
                            >
                              <i className="fas fa-trash" />
                            </button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Mobile Card List ── */}
          <div className="pbi-card-list">
            {filteredClients.length === 0 ? (
              <div className="pbi-empty">
                <div className="pbi-empty-icon"><i className="fas fa-mobile-alt" /></div>
                <div className="pbi-empty-msg">No clients match the selected filters.</div>
                <button className="pbi-empty-link" onClick={resetFilters}>Clear filters</button>
              </div>
            ) : (
              filteredClients.map(({ user: u }, index) => (
                <div key={u._id || index} className="pbi-mobile-card">
                  <div className="pbi-mobile-card-top">
                    <span className="pbi-avatar-lg" style={{ background: getAvatarColor(u.name) }}>
                      {getInitials(u.name)}
                    </span>
                    <div className="pbi-mobile-card-body">
                      <div className="pbi-mobile-card-name" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                        {u.name || <em style={{ color: "#8a9ab0", fontWeight: 500 }}>{NO_NAME_YET}</em>}
                        {u.isWalkIn ? <WalkInBadge /> : null}
                      </div>
                      <div className="pbi-mobile-card-email">{[contactLines(u).phone, contactLines(u).email].filter(Boolean).join("  ·  ") || "—"}</div>
                      <div className="pbi-mobile-card-badges">
                        <PlatformBadge platform={u.lastLoginPlatform} />
                        <LoginBadge count={u.loginCount || 0} />
                        {u.createdBy && (
                          <span style={{
                            fontSize: "0.68rem", color: "#8a9ab0", marginLeft: 6,
                            fontStyle: "italic"
                          }}>
                            by {u.createdBy.name || "Staff"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="pbi-mobile-card-actions">
                      <button
                        className="pbi-btn pbi-btn-edit"
                        title="Edit Client"
                        onClick={() => {
                          dispatch(getMobileUser(u._id, user?.result?._id, user?.result?.user_role));
                          history.push(`/admin/user-edit/${u._id}`);
                        }}
                      >
                        <i className="fas fa-edit" />
                      </button>
                      <button
                        className="pbi-btn pbi-btn-del"
                        title="Delete Client"
                        onClick={() => { setSelectedClientId(u._id); setShowDeleteModal(true); }}
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </div>
                  </div>
                  <div className="pbi-mobile-card-meta">
                    <span className="pbi-mobile-card-date">
                      <i className="fas fa-calendar-alt" style={{ marginRight: 4, opacity: 0.6 }} />
                      Joined {moment(u.createdAt).format("DD MMM YYYY")}
                    </span>
                    <span className="pbi-mobile-card-date">
                      <i className="fas fa-clock" style={{ marginRight: 4, opacity: 0.6 }} />
                      {u.lastLogin ? moment(u.lastLogin).fromNow() : "Never logged in"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default MobileUsers;
